/**
 * Admin-Ansicht: ein Konto im Detail (seit 16.10.2026). Abrechnung, Runs,
 * Zielgruppen, Studien, Schlüssel und die letzten Ledger-Einträge, alles ohne
 * Inhalte (admin.ts). Ganz unten das Löschen: zur Bestätigung die Adresse des
 * Kontos, derselbe Ablauf wie der Knopf im eigenen Konto (Abo kündigen,
 * Datenbank, Anmeldung, Bilder).
 */

import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiFehler, ladeAdminKonto, loescheFremdesKonto, type AdminKontoDetail } from '../api';
import { useSitzung } from '../session';
import { Abschnitt, AppRahmen, Hinweis, Kennzahl, Marke, Panel, Platzhalter, Seitenkopf, Verweis, Zeile } from '../ui';
import { datum, mehrzahl, nichtLoeschbar, zahl } from '../admin-anzeige';
import { t } from '../sprache';

export default function AdminKonto() {
  const { id = '' } = useParams();
  const { session, abmelden } = useSitzung();
  const [d, setD] = useState<AdminKontoDetail | null>(null);
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(true);

  const holen = useCallback(async () => {
    setLaedt(true);
    setFehler('');
    try {
      setD(await ladeAdminKonto(id));
    } catch (e) {
      const f = e as ApiFehler;
      setFehler(f.status === 404 ? t('Dieses Konto gibt es nicht, oder diese Seite gibt es für dein Konto nicht.', 'This account does not exist, or this page is not available for your account.') : f.message);
      if (f.abgemeldet) void abmelden();
    } finally {
      setLaedt(false);
    }
  }, [id, abmelden]);

  useEffect(() => { void holen(); }, [holen]);

  const k = d?.konto;
  const a = d?.abrechnung;
  return (
    <AppRahmen email={session?.user?.email ?? ''} abmelden={() => void abmelden()}>
      <p className="mt-0 mb-3 text-[0.875rem]"><Verweis to="/admin">{t('← Alle Konten', '← All accounts')}</Verweis></p>
      <Seitenkopf titel={k?.email || k?.name || id} unter={<span className="font-mono">{id}</span>} leise />

      {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
      {laedt && !d && <Panel><Zeile><Platzhalter zeilen={4} /></Zeile></Panel>}

      {d && k && a && (
        <>
          <Panel className="grid grid-cols-2 gap-5 px-4 py-5 sm:grid-cols-4 sm:px-6">
            <Kennzahl label={t('Guthaben', 'Balance')} wert={a.unbegrenzt ? t('unbegrenzt', 'unlimited') : typeof a.guthaben === 'number' ? zahl(a.guthaben) : '—'} />
            <Kennzahl label={t('Runs', 'Runs')} wert={zahl(d.zaehlung?.runs ?? d.runs.length)} />
            <Kennzahl label={t('Zielgruppen', 'Audiences')} wert={zahl(d.zielgruppen.length)} />
            <Kennzahl label={t('Studien', 'Studies')} wert={zahl(d.studien.length)} />
          </Panel>

          <Abschnitt titel={t('Konto', 'Account')}>
            <Panel>
              <Feld name={t('Registriert', 'Signed up')}>{datum(k.erstellt, true)}</Feld>
              <Feld name={t('Anmeldung', 'Sign-in')}>
                {d.anmeldung
                  ? <>{d.anmeldung.anbieter ?? t('ja', 'yes')} · {d.anmeldung.bestaetigt ? t(`bestätigt ${datum(d.anmeldung.bestaetigt)}`, `confirmed ${datum(d.anmeldung.bestaetigt)}`) : t('nicht bestätigt', 'not confirmed')} · {t('zuletzt', 'last')} {datum(d.anmeldung.zuletzt, true)}</>
                  : t('keine (nur API-Schlüssel)', 'none (API key only)')}
              </Feld>
              <Feld name={t('Erster Aufruf aus Claude', 'First call from Claude')}>{datum(k.erster_aufruf, true)}</Feld>
              <Feld name={t('Rolle', 'Role')}>{k.role}{k.admin && <> · <Marke art="marke">Admin</Marke></>}</Feld>
              <Feld name={t('Abo', 'Subscription')}>
                {a.abo
                  ? <>{a.abo.plan ?? '—'} · {a.abo.status ?? '—'}{a.abo.bis ? t(` · bis ${datum(a.abo.bis)}`, ` · until ${datum(a.abo.bis)}`) : ''}{a.abo.ab_naechster_zahlung ? t(` · ab nächster Zahlung ${a.abo.ab_naechster_zahlung}`, ` · from next payment ${a.abo.ab_naechster_zahlung}`) : ''}{a.abo.mollie ? ' · Mollie' : ''}</>
                  : t('keins', 'none')}
              </Feld>
              {a.notiz && <Feld name={t('Notiz', 'Note')}>{a.notiz}</Feld>}
            </Panel>
          </Abschnitt>

          <Liste titel={t('Runs', 'Runs')} zahl={d.zaehlung?.runs} unter={(d.zaehlung?.runs ?? 0) > d.runs.length ? t(`Die letzten ${d.runs.length}.`, `The last ${d.runs.length}.`) : undefined} leer={t('Noch kein Run.', 'No run yet.')} zeilen={d.runs.map(r => ({
            schluessel: r.id,
            oben: <>{datum(r.zeit, true)} · {r.art ?? '—'}{r.textart ? ` · ${r.textart}` : ''}</>,
            unten: [t(`${zahl(r.credits)} Credits`, mehrzahl(r.credits, 'credit', 'credits')), r.personas ? t(`${r.personas} Personas`, mehrzahl(r.personas, 'persona', 'personas')) : null, r.urteil, r.studie, r.modell].filter(Boolean).join(' · '),
          }))} />

          <Liste titel={t('Zielgruppen', 'Audiences')} leer={t('Keine eigene Zielgruppe.', 'No audience of its own.')} zeilen={d.zielgruppen.map(s => ({
            schluessel: s.slug,
            oben: s.name,
            unten: [t(`${s.personas} Personas`, mehrzahl(s.personas, 'persona', 'personas')), s.sichtbarkeit, t(`seit ${datum(s.erstellt)}`, `since ${datum(s.erstellt)}`), s.kopie_von ? t(`Kopie von ${s.kopie_von}`, `copy of ${s.kopie_von}`) : null].filter(Boolean).join(' · '),
          }))} />

          <Liste titel={t('Studien und Berichte', 'Studies and reports')} leer={t('Keine Studie.', 'No study.')} zeilen={d.studien.map(s => ({
            schluessel: s.id,
            oben: s.name,
            unten: [t(`seit ${datum(s.erstellt)}`, `since ${datum(s.erstellt)}`), t(`${s.berichte} Berichte`, mehrzahl(s.berichte, 'report', 'reports')), s.offene_links ? t(`${s.offene_links} mit offenem Link`, `${s.offene_links} with an open link`) : null].filter(Boolean).join(' · '),
          }))} />

          <Liste titel={t('API-Schlüssel', 'API keys')} leer={t('Kein Schlüssel.', 'No key.')} zeilen={d.schluessel.map(s => ({
            schluessel: s.praefix + (s.erstellt ?? ''),
            oben: <><span className="font-mono">{s.praefix}…</span>{s.name ? ` · ${s.name}` : ''}{s.widerrufen && <> · <Marke art="schlecht">{t('widerrufen', 'revoked')}</Marke></>}</>,
            unten: [t(`angelegt ${datum(s.erstellt)}`, `created ${datum(s.erstellt)}`), s.zuletzt ? t(`zuletzt ${datum(s.zuletzt, true)}`, `last used ${datum(s.zuletzt, true)}`) : t('nie benutzt', 'never used')].join(' · '),
          }))} />

          <Abschnitt titel="Ledger" zahl={d.ledger.length} unter={t('Die letzten 50 Buchungen, neueste zuerst. Das Guthaben oben rechnet noch nicht gebuchte Verfälle mit ein.', 'The last 50 entries, newest first. The balance above includes expiries not yet booked.')}>
            {d.ledger.length === 0
              ? <Panel className="px-4 py-4 text-[0.9375rem] text-text-leise sm:px-5">{t('Keine Buchung.', 'No entry.')}</Panel>
              : (
                <Panel>
                  {d.ledger.map((e, i) => (
                    <Zeile key={i} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <span className="text-[0.875rem] text-text">{datum(e.zeit, true)} · {e.grund}{e.notiz ? ` · ${e.notiz}` : ''}{e.verfaellt ? t(` · verfällt ${datum(e.verfaellt)}`, ` · expires ${datum(e.verfaellt)}`) : ''}</span>
                      <span className={`text-[0.875rem] font-semibold tabular-nums ${e.delta < 0 ? 'text-text-leise' : 'text-gut'}`}>{e.delta > 0 ? '+' : ''}{zahl(e.delta)}</span>
                    </Zeile>
                  ))}
                </Panel>
              )}
          </Abschnitt>

          <Loeschen id={k.id} email={k.email ?? ''} grund={nichtLoeschbar(k, null)} abo={!!a.abo?.mollie && a.abo.status !== 'canceled'} />
        </>
      )}
    </AppRahmen>
  );
}

function Feld({ name, children }: { name: string; children: ReactNode }) {
  return (
    <Zeile className="grid gap-1 sm:grid-cols-[13rem_1fr] sm:gap-4">
      <span className="text-[0.8125rem] font-medium text-text-leise">{name}</span>
      <span className="text-[0.875rem] [overflow-wrap:anywhere] text-text">{children}</span>
    </Zeile>
  );
}

function Liste({ titel, leer, zeilen, zahl: gesamt, unter }: {
  titel: string; leer: string; zeilen: Array<{ schluessel: string; oben: ReactNode; unten: string }>;
  /** Gesamtzahl, wenn die Liste gekürzt ist. */
  zahl?: number; unter?: string;
}) {
  return (
    <Abschnitt titel={titel} zahl={gesamt ?? zeilen.length} unter={unter}>
      {zeilen.length === 0
        ? <Panel className="px-4 py-4 text-[0.9375rem] text-text-leise sm:px-5">{leer}</Panel>
        : (
          <Panel>
            {zeilen.map(z => (
              <Zeile key={z.schluessel}>
                <span className="block text-[0.9375rem] [overflow-wrap:anywhere] text-tinte">{z.oben}</span>
                {z.unten && <span className="mt-0.5 block text-[0.8125rem] text-text-hell">{z.unten}</span>}
              </Zeile>
            ))}
          </Panel>
        )}
    </Abschnitt>
  );
}

function Loeschen({ id, email, grund, abo }: { id: string; email: string; grund: string | null; abo: boolean }) {
  const navigate = useNavigate();
  const [eingabe, setEingabe] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const passt = !!email && eingabe.trim().toLowerCase() === email.trim().toLowerCase();

  async function absenden(e: FormEvent) {
    e.preventDefault();
    if (!passt) return;
    setFehler('');
    setLaedt(true);
    try {
      await loescheFremdesKonto(id, eingabe.trim());
      navigate(`/admin?geloescht=${encodeURIComponent(email || id)}`, { replace: true });
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : t('Das hat nicht geklappt. Versuch es noch einmal.', 'That did not work. Try again.'));
      setLaedt(false);
    }
  }

  return (
    <Abschnitt titel={t('Konto löschen', 'Delete account')}>
      <Panel className="px-4 py-4 sm:px-5">
        {grund ? (
          <p className="m-0 text-[0.9375rem] text-text-leise">{grund}</p>
        ) : !email ? (
          <p className="m-0 text-[0.9375rem] text-text-leise">{t('Das Konto hat keine E-Mail-Adresse. Löschen mit scripts/konto-loeschen.mjs.', 'The account has no email address. Delete it with scripts/konto-loeschen.mjs.')}</p>
        ) : (
          <form onSubmit={absenden}>
            <p className="m-0 text-[0.875rem] leading-relaxed text-text">
              {t(
                <>Löscht das Konto mit Anmeldung, Schlüsseln, Guthaben, eigenen Zielgruppen, Runs, Studien, Berichten und Bildschirmfotos.
                {abo && ' Das laufende Abo wird vorher bei Mollie gekündigt.'} Rechnungen bleiben. Rückgängig machen lässt sich das nicht.</>,
                <>Deletes the account with sign-in, keys, balance, its own audiences, runs, studies, reports and screenshots.
                {abo && ' The running subscription is canceled at Mollie first.'} Invoices stay. This cannot be undone.</>,
              )}
            </p>
            {fehler && <div className="mt-3"><Hinweis art="schlecht">{fehler}</Hinweis></div>}
            <label className="mt-3 block text-[0.8125rem] font-medium text-text">
              {t('Zur Bestätigung die E-Mail-Adresse des Kontos', 'To confirm, the account’s email address')}
              <input
                type="email" autoComplete="off" spellCheck={false} value={eingabe} onChange={e => setEingabe(e.target.value)} disabled={laedt}
                placeholder={email}
                className="mt-1.5 block h-11 w-full max-w-sm rounded-[0.625rem] border border-linie-stark bg-flaeche px-3 text-[1rem] text-tinte focus:border-marke focus:outline-none"
              />
            </label>
            <button
              type="submit" disabled={!passt || laedt}
              className="mt-3 inline-flex min-h-10 cursor-pointer items-center rounded-full border-0 bg-schlecht px-4 text-[0.875rem] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-45"
            >
              {laedt ? t('Wird gelöscht …', 'Deleting …') : t('Endgültig löschen', 'Delete permanently')}
            </button>
          </form>
        )}
      </Panel>
    </Abschnitt>
  );
}
